import { useEffect, useRef, useState } from 'react';
import { Sparkles, X, Send } from 'lucide-react';
import { api } from '../../api/client';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const MESSAGE_ACCUEIL: Message = {
  role: 'assistant',
  content: "Bonjour ! Je peux vous aider à comprendre comment utiliser TacTac — nouvelle demande, calendrier, rôles… Que puis-je vous expliquer ?",
};

// Assistant flottant, niveau 1 : aide statique sur le fonctionnement de
// l'app (aucune donnée réelle). Voir server/services/chatbot.ts.
export function ChatBubble() {
  const [ouvert, setOuvert] = useState(false);
  const [messages, setMessages] = useState<Message[]>([MESSAGE_ACCUEIL]);
  const [saisie, setSaisie] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const finRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ouvert) finRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, ouvert]);

  async function envoyer() {
    const texte = saisie.trim();
    if (!texte || envoi) return;
    const historique = [...messages, { role: 'user' as const, content: texte }];
    setMessages(historique);
    setSaisie('');
    setErreur('');
    setEnvoi(true);
    try {
      const { reponse } = await api.sendChatMessage(historique);
      setMessages((prev) => [...prev, { role: 'assistant', content: reponse }]);
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "L'assistant n'a pas pu répondre.");
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div className="chatbubble-wrap">
      {ouvert && (
        <div className="chatbubble-panel card">
          <div className="chatbubble-header">
            <span className="flex items-center gap-8"><Sparkles className="icon-sm" /> Assistant · TacTac</span>
            <button className="btn btn-icon btn-ghost btn-sm" onClick={() => setOuvert(false)} aria-label="Fermer">
              <X className="icon-sm" />
            </button>
          </div>
          <div className="chatbubble-messages">
            {messages.map((m, i) => (
              <div key={i} className={`chatbubble-msg chatbubble-msg-${m.role}`}>{m.content}</div>
            ))}
            {envoi && (
              // Trois points qui bougent (une roue de 14 px dans une bulle passait pour une barre fixe) ; les lecteurs d'écran
              // entendent « L'assistant réfléchit… » ; avec « réduire les animations », les points restent fixes.
              <div className="chatbubble-msg chatbubble-msg-assistant" role="status" aria-live="polite">
                <span className="chatbubble-dots" aria-hidden="true"><span /><span /><span /></span>
                <span className="sr-only">L'assistant réfléchit…</span>
              </div>
            )}
            {erreur && <div className="chatbubble-msg chatbubble-msg-erreur">{erreur}</div>}
            <div ref={finRef} />
          </div>
          <div className="chatbubble-input-row">
            <input
              type="text"
              className="form-input"
              placeholder="Poser une question…"
              value={saisie}
              disabled={envoi}
              onChange={(e) => setSaisie(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') envoyer(); }}
            />
            <button className="btn btn-icon" onClick={envoyer} disabled={envoi || !saisie.trim()} aria-label="Envoyer">
              <Send className="icon-sm" />
            </button>
          </div>
        </div>
      )}
      <button
        className="chatbubble-toggle"
        onClick={() => setOuvert((v) => !v)}
        aria-label={ouvert ? "Fermer l'assistant" : "Ouvrir l'assistant"}
      >
        {ouvert ? <X className="icon-md" /> : <Sparkles className="icon-md" />}
      </button>
    </div>
  );
}
